import React, { useState } from "react";
import { ASSET_SECTION_IDS, LIABILITY_SECTION_IDS, STRUCTURE_SECTION_IDS } from "../data";
import { getMissingRequiredFields, isBlankValue, shouldPair } from "../utils";
import DocumentsField from "./DocumentsField";
import FieldControl from "./FieldControl";
import SectionIcon from "./SectionIcon";

const getItemName = (section, item, fallback) => item[section.nameKey] || item.nombre || fallback;

const buildNotesField = (copy) => ({
  key: "notes",
  label: copy.form.notes,
  type: "textarea",
  placeholder: copy.form.notes_placeholder,
});

const buildLinkedOptions = (field, data, sections, copy) => {
  if (!["linked_records", "linked_select", "asset_link_selector"].includes(field.type)) return [];

  const sectionIds = field.linkedTarget === "structures"
    ? STRUCTURE_SECTION_IDS
    : field.linkedTarget === "liabilities"
      ? LIABILITY_SECTION_IDS
      : ASSET_SECTION_IDS;
  return sectionIds.reduce((options, linkedSectionId) => {
    const linkedSection = sections[linkedSectionId];
    const items = data[linkedSectionId] || [];
    return options.concat(items.map((linkedItem, index) => ({
      id: linkedItem.estate_mapping_id || `${linkedSection.payloadKey}:${index}`,
      type: linkedSection.payloadKey,
      sectionId: linkedSectionId,
      sectionTitle: linkedSection.title,
      value: getItemName(linkedSection, linkedItem, copy.list.no_name),
    })));
  }, []);
};

const manualStructuresFrom = (item, createRecordId) => (
  (item.estructura_vinculada || [])
    .filter((record) => record && record.manual && record.type === "structures" && record.value)
    .map((record) => ({
      estate_mapping_id: record.id || createRecordId(),
      nombre: record.value,
      type: record.structureType || "Trust",
    }))
);

const manualLiabilitiesFrom = (item, createRecordId) => (
  (item.pasivo_vinculado || [])
    .filter((record) => record && record.manual && record.type === "liabilities" && record.value)
    .map((record) => ({
      estate_mapping_id: record.id || createRecordId(),
      nombre: record.value,
    }))
);

const linkedAssetsForLiability = (liability, data, sections) => {
  const existing = Array.isArray(liability.activo_vinculado) ? liability.activo_vinculado : [];
  if (!liability.estate_mapping_id) return existing;

  const reverseLinks = ASSET_SECTION_IDS.reduce((links, sectionId) => {
    const section = sections[sectionId];
    return links.concat((data[sectionId] || [])
      .filter((asset) => Array.isArray(asset.pasivo_vinculado) && asset.pasivo_vinculado.some((link) => (
        link.type === "liabilities" && link.id === liability.estate_mapping_id
      )))
      .map((asset, index) => ({
        type: section.payloadKey,
        id: asset.estate_mapping_id || `${section.payloadKey}:${index}`,
        value: getItemName(section, asset, "Sin nombre"),
      })));
  }, []);

  return existing.concat(reverseLinks.filter((link) => !existing.some((current) => current.type === link.type && current.id === link.id)));
};

const linkedRecordsForStructure = (structure, data, sections, sectionIds, linkedField) => {
  const existing = Array.isArray(structure[linkedField]) ? structure[linkedField] : [];
  if (!structure.estate_mapping_id) return existing;

  const reverseLinks = sectionIds.reduce((links, sectionId) => {
    const section = sections[sectionId];
    return links.concat((data[sectionId] || [])
      .filter((record) => Array.isArray(record.estructura_vinculada) && record.estructura_vinculada.some((link) => (
        link.type === "structures" && link.id === structure.estate_mapping_id
      )))
      .map((record, index) => ({
        type: section.payloadKey,
        id: record.estate_mapping_id || `${section.payloadKey}:${index}`,
        value: getItemName(section, record, "Sin nombre"),
      })));
  }, []);

  return existing.concat(reverseLinks.filter((link) => !existing.some((current) => current.type === link.type && current.id === link.id)));
};

const defaultFormState = (createRecordId, section) => ({ estate_mapping_id: createRecordId(section) });

const formStateFromItem = (sectionId, item, data, sections) => {
  if (!item) return item;
  const structureType = {
    Trust: "Trust",
    Fideicomiso: "Trust",
    Corporation: "Corporation",
    Corporacion: "Corporation",
    "Corporación": "Corporation",
  }[item.type || item.tipo];

  return {
    ...item,
    ...(sectionId === "estructuras" && structureType ? { type: structureType } : {}),
    ...(sectionId === "estructuras" ? {
      activo_vinculado: linkedRecordsForStructure(item, data, sections, ASSET_SECTION_IDS, "activo_vinculado"),
      pasivo_vinculado: linkedRecordsForStructure(item, data, sections, LIABILITY_SECTION_IDS, "pasivo_vinculado"),
    } : {}),
    ...(sectionId === "pasivos" ? { activo_vinculado: linkedAssetsForLiability(item, data, sections) } : {}),
  };
};

const FormStep = ({ copy, createRecordId, documents, documentsEnabled, onDeleteDocument, onUploadDocument, sectionId, sections, data, item, onCancel, onSave, returnToReview }) => {
  const section = sections[sectionId];
  const [formState, setFormState] = useState(item
    ? formStateFromItem(sectionId, item, data, sections)
    : defaultFormState(createRecordId, section));
  const [requiredErrors, setRequiredErrors] = useState([]);
  const [participationBlurred, setParticipationBlurred] = useState(false);
  const participation = Number.parseFloat(String(formState.participacion || "").replace(",", "."));
  const needsOtherOwners = !Number.isNaN(participation) && participation < 100;
  const requiresOtherOwners = participationBlurred && needsOtherOwners;
  const hasLoanType = sectionId === "pasivos" && section.fields.some((field) => field.key === "tipo_prestamo");
  const requiresInterestRateFrequency = hasLoanType && ["Installment", "A plazos"].includes(formState.tipo_prestamo);

  const hasError = (field) => requiredErrors.includes(field.key);
  const handleChange = (key, value) => {
    setFormState((current) => {
      if (sectionId === "estructuras" && key === "type") {
        const { tipo, ...structure } = current;
        return { ...structure, type: value };
      }
      return { ...current, [key]: value };
    });
    setRequiredErrors((current) => current.filter((fieldKey) => fieldKey !== key));
  };
  const handleSave = () => {
    const missingRequiredFields = getMissingRequiredFields(section.fields, formState);
    if (requiresInterestRateFrequency && isBlankValue(formState.interest_rate_frequency)) missingRequiredFields.push("interest_rate_frequency");
    if (Array.isArray(formState.pasivo_vinculado) && formState.pasivo_vinculado.some((record) => (
      record && record.manual && record.type === "liabilities" && isBlankValue(record.value)
    ))) missingRequiredFields.push("pasivo_vinculado");
    if (missingRequiredFields.length > 0) {
      setRequiredErrors(missingRequiredFields);
      return;
    }
    onSave(
      formState,
      manualStructuresFrom(formState, () => createRecordId(sections.estructuras)),
      manualLiabilitiesFrom(formState, () => createRecordId(sections.pasivos))
    );
  };
  const fields = [];
  const notesField = buildNotesField(copy);

  for (let index = 0; index < section.fields.length; index += 1) {
    const field = section.fields[index];
    const nextField = section.fields[index + 1];
    const fieldHidden = (field.key === "otros_duenos" && !requiresOtherOwners) || (field.key === "interest_rate_frequency" && hasLoanType && !requiresInterestRateFrequency);
    const nextFieldHidden = nextField && ((nextField.key === "otros_duenos" && !requiresOtherOwners) || (nextField.key === "interest_rate_frequency" && hasLoanType && !requiresInterestRateFrequency));
    const fieldWithConditionalOptions = field.optionsByField
      ? { ...field, options: field.optionsByValue[formState[field.optionsByField]] || [] }
      : field;
    const nextFieldWithConditionalOptions = nextField && nextField.optionsByField
      ? { ...nextField, options: nextField.optionsByValue[formState[nextField.optionsByField]] || [] }
      : nextField;
    const displayedField = fieldWithConditionalOptions.key === "otros_duenos"
      ? { ...fieldWithConditionalOptions, required: false }
      : fieldWithConditionalOptions.key === "interest_rate_frequency"
        ? { ...fieldWithConditionalOptions, required: requiresInterestRateFrequency }
        : fieldWithConditionalOptions;
    const displayedNextField = nextFieldWithConditionalOptions && (nextFieldWithConditionalOptions.key === "otros_duenos"
      ? { ...nextFieldWithConditionalOptions, required: false }
      : nextFieldWithConditionalOptions.key === "interest_rate_frequency"
        ? { ...nextFieldWithConditionalOptions, required: requiresInterestRateFrequency }
        : nextFieldWithConditionalOptions);
    if (shouldPair(field, nextField)) {
      const fieldLinkedOptions = buildLinkedOptions(field, data, sections, copy);
      const nextFieldLinkedOptions = buildLinkedOptions(nextField, data, sections, copy);
      fields.push(
        <div className="estate-mapping__field-row" key={`${field.key}-${nextField.key}`}>
          <FieldControl copy={copy} currencyType={formState.currency_type} error={hasError(field)} field={displayedField} hidden={fieldHidden} linkedOptions={fieldLinkedOptions} value={formState[field.key]} onBlur={(key) => key === "participacion" && setParticipationBlurred(true)} onChange={handleChange} />
          <FieldControl copy={copy} currencyType={formState.currency_type} error={hasError(nextField)} field={displayedNextField} hidden={nextFieldHidden} linkedOptions={nextFieldLinkedOptions} value={formState[nextField.key]} onBlur={(key) => key === "participacion" && setParticipationBlurred(true)} onChange={handleChange} />
        </div>
      );
      index += 1;
    } else {
      fields.push(<FieldControl copy={copy} currencyType={formState.currency_type} error={hasError(field)} key={field.key} field={displayedField} hidden={fieldHidden} linkedOptions={buildLinkedOptions(field, data, sections, copy)} value={formState[field.key]} onBlur={(key) => key === "participacion" && setParticipationBlurred(true)} onChange={handleChange} />);
    }
  }
  const formModeLabel = item ? copy.form.edit_record : copy.form.new_record;
  const subtitle = section.title === section.pillar ? formModeLabel : `${formModeLabel} - ${section.pillar}`;

  return (
    <section className="estate-mapping__content">
      <button className="estate-mapping__back-link" onClick={onCancel} type="button">{returnToReview ? copy.form.back_to_review : copy.form.back_to_list}</button>
      <div className="estate-mapping__section-heading">
        <SectionIcon section={section} />
        <div><h2>{section.title}</h2><p>{subtitle}</p></div>
      </div>
      <div className="estate-mapping__form">
        {fields}
        <FieldControl copy={copy} currencyType={formState.currency_type} field={notesField} key={notesField.key} value={formState[notesField.key]} onChange={handleChange} />
      </div>
      <DocumentsField
        copy={copy}
        documents={(documents || []).filter((document) => document.estate_mapping_record_id === formState.estate_mapping_id)}
        enabled={documentsEnabled}
        onDelete={onDeleteDocument}
        onUpload={(file) => onUploadDocument(sectionId, formState.estate_mapping_id, file)}
      />
      <div className="estate-mapping__footer-actions">
        <button className="estate-mapping__button estate-mapping__button--secondary" onClick={onCancel}>{copy.buttons.cancel}</button>
        <button className="estate-mapping__button estate-mapping__button--primary" onClick={handleSave}>{copy.buttons.save_record}</button>
      </div>
    </section>
  );
};

export default FormStep;
